import AddEpisodeButton from '../Components/EpisodeActions/AddEpisodeButton';
import DrawerHeader from './DrawerHeader';
import LogoWithIcon from '../assets/LogoWithIcon.png';
import {
    Autocomplete,
    Box,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemSecondaryAction,
    ListItemText,
    TextField,
    Drawer as MuiDrawer, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShowActions from '../Components/ShowActions';
import SeasonActions from '../Components/SeasonActions';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { EpisodeMoreActions } from '../Components/EpisodeActions';
import React, { useContext, useRef } from 'react';
import { SeasonShowContext } from '../Providers/SeasonShowProvider';
import { FragmentType, getFragmentData, graphql } from 'gql';

const DrawerEpisodeData = graphql(`
    fragment DrawerEpisodeData on Query {
        episodes(where: $where, options: $options) {
            id
            title
        }
    }`);

type DrawerProps = {
    open?: boolean
    drawerWidth: number
    data?: FragmentType<typeof DrawerEpisodeData>
    handleDrawerClose: () => void
}

export default function Drawer({ open, drawerWidth, handleDrawerClose, data }: DrawerProps) {
    const episodesData = getFragmentData(DrawerEpisodeData, data);

    const { shows, seasons, selectedShow, selectedSeason, setSelectedShow, setSelectedSeason } = useContext(SeasonShowContext);

    const navigate = useNavigate();
    const params = useParams();

    const ref = useRef<HTMLUListElement>(null);

    return (
        <MuiDrawer
            sx={{
                width: drawerWidth,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: drawerWidth,
                    boxSizing: 'border-box',
                },
                '@media print': {
                    display: 'none',
                },
            }}
            variant='persistent'
            anchor='left'
            open={open}
        >
            <DrawerHeader style={{ justifyContent: 'space-between' }}>
                <img style={{ width: 200, marginLeft: 20 }} src={LogoWithIcon} alt='Audiolo Logo'/>
                <IconButton onClick={handleDrawerClose}>
                    <CloseIcon />
                </IconButton>
            </DrawerHeader>
            <Divider style={{ marginBottom: 8 }}/>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Autocomplete
                    disablePortal
                    id="show-autocomplete"
                    options={shows ?? []}
                    getOptionLabel={o => o.title}
                    loading={!shows}
                    sx={{ width: '100%', pt: 1, pb: 1, pl: 1 }}
                    renderInput={(params) => <TextField {...params} label="Shows" />}
                    onChange={(_e, v) => {
                        setSelectedShow(v);
                        setSelectedSeason(null);
                        navigate('/');
                    }}
                    value={selectedShow}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    disabled={!shows || shows.length === 0}
                />
                <ShowActions />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Autocomplete
                    disablePortal
                    id="seasons-autocomplete"
                    options={seasons ?? []}
                    getOptionLabel={o => o.title}
                    loading={!!shows && shows.length > 0 && !seasons}
                    sx={{ width: '100%', pt: 1, pb: 1, pl: 1 }}
                    renderInput={(params) => <TextField {...params} label="Seasons" />}
                    onChange={(_e, v) => {
                        setSelectedSeason(v)
                        navigate('/');
                    }}
                    value={selectedSeason}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    disabled={!seasons || seasons.length === 0}
                />
                <SeasonActions />
            </Box>
            {selectedSeason && selectedSeason.totalVoices > 0 && (
                <Typography sx={{ mx: 'auto', mb: 1 }}>Total Voices: {selectedSeason.totalVoices}</Typography>
            )}
            <Divider/>
            <List ref={ref} sx={{ padding: 0, overflow: 'auto' }}>
                {episodesData && episodesData.episodes && episodesData.episodes.map(e => (
                    <ListItem key={e.id} sx={{ p: 0, secondaryAction: { right: 0 }}}>
                        <ListItemButton
                            key={e.id}
                            component={RouterLink}
                            to={`/episode/${e.id}`}
                            selected={e.id === params.episodeId}
                        >
                            <ListItemText primary={e.title} />
                        </ListItemButton>
                        <ListItemSecondaryAction sx={{ right: 0 }}>
                            <EpisodeMoreActions episode={e} />
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}
            </List>
            {episodesData && episodesData.episodes && selectedSeason && (
                <Box sx={{
                    width: 'fit-content',
                    mx: 'auto',
                    my: 1,
                }}>
                    <AddEpisodeButton />
                </Box>
            )}
        </MuiDrawer>
    );
}